import { ContainerStackScreen } from "@Components/ContainerStackScreen"
import { Button } from "@Components/CustomButton/Button"
import { BreeSerifText } from "@Components/CustomText/BreeSerifText"
import { OpenSansText } from "@Components/CustomText/OpenSansText"
import { InputGroup } from "@Components/Input/InputGroups"
import { Spacer } from "@Components/Space"
import React, { useContext, useEffect, useState } from "react"
import DateTimePicker from "@react-native-community/datetimepicker"
import { baseColors } from "@Constants/baseStyles"
import { CustomScrollview } from "@Components/CustomScrollView/CustomScrollView"
import { CustomPicker } from "@Components/FollowUp/Toolbox/CustomPicker"
import { useRecoilValue } from "recoil"
import { userAtom } from "@Recoil/Atom/userAtom"
import { UserService } from "@Services/User.service"
import { ShowToast } from "@Components/ToastNotification/ToastNotification"
import { dbDateFormat, localDateFormat } from "@Utils/utils"
import { constantTypesAtom } from "@Recoil/Atom/constantsAtom"
import { ConstantsContext } from "@Providers/ConstantsProvider"
import { LocalStorage } from "@Services/sharedPreferences/localStorage"
import { FollowUpContext } from "@Providers/FollowUpProvider"
import { Platform, TouchableOpacity } from "react-native"
import { useRoute } from "@react-navigation/native"

const moment = require("moment")

const defaultDate = {
    date: moment(new Date(), dbDateFormat).format(dbDateFormat)
}

export const ConstantsFollowUpForm = ({ navigation }) => {
    const user = useRecoilValue(userAtom)

    const routes = useRoute()
    const constantToEdit = routes?.params?.constantToEdit
    const defaultConstant = {
        userId: user?.id,
        typeId: null,
        value: "",
        ...defaultDate
    }
    const [creating, setCreating] = useState(false)
    const [date, setDate] = useState(new Date().toISOString().split("T")[0])
    const { setUpdateList } = useContext(ConstantsContext)
    const { setConstantDate } = useContext(FollowUpContext)
    const [showDatePicker, setShowDatePicker] = useState(false)
    const [hasEnoughCategories, setHasEnoughCategories] = useState(false)
    const [checkedDate, setCheckedDate] = useState(new Date())
    const constantTypes = useRecoilValue(constantTypesAtom)
    const constantTypesWithoutIMC = constantTypes?.filter((constant) => constant?.id !== 3) ?? []
    const [constantPayload, setConstantPayload] = useState(defaultConstant)
    const startDate = moment(constantPayload?.date, dbDateFormat).subtract(1, "d").format(dbDateFormat)

    useEffect(() => {
        if (constantToEdit?.id > 0) {
            setConstantPayload({
                userId: user?.id,
                typeId: constantToEdit?.type,
                value: constantToEdit?.value,
                date: moment(constantToEdit?.date, dbDateFormat).format(dbDateFormat)
            })
            setCheckedDate(constantToEdit?.date)
        }
    }, [constantToEdit])

    const onChangeDate = (event, selectedDate) => {
        const currentDate = selectedDate
        setShowDatePicker((prev) => !prev)
        if (currentDate) {
            setCheckedDate(currentDate)
            setConstantPayload((old) => ({
                ...old,
                date: moment(currentDate, dbDateFormat).format(dbDateFormat)
            }))
        }
    }

    const onChangeWebDate = (dateValue) => {
        const currentDate = dateValue
        if (currentDate) {
            setCheckedDate(currentDate)
            setConstantPayload((old) => ({
                ...old,
                date: moment(currentDate, dbDateFormat).format(dbDateFormat)
            }))
        }
    }

    const successfull = (data) => {
        ShowToast({ type: "success", message: "Vos constantes ont été enregistrées" })
        setCreating(false)
        setUpdateList((prev) => !prev)
        setConstantDate(data?.date)
        LocalStorage.setJustCreatedConstantDate(data?.date)
        setConstantPayload(defaultConstant)
        navigation.goBack()
    }

    const failed = () => {
        setCreating(false)
        ShowToast({ type: "error", message: "Oops une erreur est survenue" })
    }

    const performSaveConstants = () => {
        setCreating(true)
        const payloadData = [constantPayload]
        if (user?.id) {
            payloadData.map((payload, i) => {
                UserService.saveConstants({ ...payload })
                    .then((res) => {
                        if (res?.data?.code === 400) {
                            setCreating(false)
                            ShowToast({ type: "error", message: "Erreur de données entrées" })
                        } else {
                            successfull()
                        }
                    })
                    .catch((e) => {
                        console.log({ e })
                        failed()
                    })
            })
        }
    }

    const isIMC = constantPayload?.typeId === 3
    const isTension = constantPayload?.typeId === 10

    const isEdition = constantToEdit?.id > 0

    const AuthorizedTensionValue = (value) => {
        const SplitedValue = value?.split("/")

        if (Number(value) === 0) {
            return true
        } else {
            if (isNaN(Number(SplitedValue?.[0])) || isNaN(Number(SplitedValue?.[1]))) {
                return false
            } else {
                if (Number(SplitedValue?.[0]) === 0 || Number(SplitedValue?.[1]) === 0) {
                    return false
                }
                return true
            }
        }
    }

    return (
        <ContainerStackScreen>
            <CustomScrollview>
                <BreeSerifText color={baseColors.green.primary} fontSize={24}>
                    Entrez vos constantes
                </BreeSerifText>

                <Spacer height={15} />

                <OpenSansText fontSize={16}>
                    Remplissez tous les champs ci-dessous et enregistrez vos résultats.
                </OpenSansText>

                <Spacer height={30} />

                <OpenSansText fontSize={16}>Choisissez une constante</OpenSansText>

                <Spacer height={20} />
                <CustomPicker
                    title="Veuillez sélectionner une constante"
                    data={constantTypesWithoutIMC}
                    disabled={creating || isEdition}
                    value={constantPayload.typeId}
                    handleChange={(id) => setConstantPayload((old) => ({ ...old, typeId: id }))}
                />

                <Spacer height={20} />

                <InputGroup
                    code={!isTension}
                    disabled={creating || isIMC}
                    validation_message="obligatoire"
                    label="Valeur de la constante"
                    placeholder={isTension ? "Entrez une valeur (ex: 126/70)" : "Entrez une valeur"}
                    value={constantPayload?.value}
                    onChange={(value) => setConstantPayload((old) => ({ ...old, value: value < 0 ? "0" : value }))}
                />

                <Spacer height={15} />

                {Platform.OS === "web" ? (
                    <input
                        disabled={creating || isEdition}
                        style={{ height: 50, borderRadius: 10, paddingRight: 20, paddingLeft: 10, fontSize: 14 }}
                        type="date"
                        value={date}
                        onChange={(e) => {
                            const dateValue = e.target.value
                            setDate(dateValue)
                            onChangeWebDate(dateValue)
                        }}
                    />
                ) : (
                    <TouchableOpacity onPress={() => (isEdition ? undefined : !creating && setShowDatePicker(true))}>
                        <InputGroup
                            editable={false}
                            disabled={creating || isEdition}
                            value={moment(checkedDate).format(localDateFormat)}
                            // onFocus={() => !creating && setShowDatePicker(true)}
                            // onTouch={() => !creating && setShowDatePicker(true)}
                            validation_message="obligatoire"
                            label="Date de prise"
                            placeholder="Sélectionner une date"
                        />
                    </TouchableOpacity>
                )}

                {showDatePicker && (
                    <DateTimePicker
                        value={checkedDate}
                        disabled={creating || isEdition}
                        mode="date"
                        is24Hour={true}
                        display="default"
                        onChange={onChangeDate}
                        maximumDate={new Date()}
                    />
                )}

                <Spacer height={50} />
            </CustomScrollview>

            <Button
                loading={creating}
                disabled={
                    creating ||
                    constantPayload?.value?.length === 0 ||
                    !constantPayload?.typeId ||
                    (isTension && !AuthorizedTensionValue(constantPayload?.value))
                }
                onPress={performSaveConstants}
                text={creating ? "Validation en cours..." : "Valider mes constantes"}
                width="80%"
                backgroundColor={baseColors.green.primary}
                labelColor="white"
            />
        </ContainerStackScreen>
    )
}
